import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import RichTextEditor from '../RichTextEditor';
import { Plus } from 'lucide-react';

const NoteFormDialog = ({
  isOpen,
  onOpenChange,
  note,
  setNote,
  patients,
  noteStatuses,
  statusesEnabled,
  onSubmit,
  isEditing = false,
  patientLabel,
  triggerButton
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {triggerButton && <DialogTrigger asChild>{triggerButton}</DialogTrigger>}
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-x-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{isEditing ? 'Modifier la note' : 'Créer une nouvelle note'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifiez le contenu de la note' : `Rédigez une note pour un ${patientLabel}`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <form onSubmit={onSubmit} className="space-y-4">
            {!isEditing && (
              <div>
                <Label htmlFor="note-patient">{patientLabel} *</Label>
                <Select 
                  value={note.patient_id} 
                  onValueChange={(value) => setNote({...note, patient_id: value})}
                  required
                >
                  <SelectTrigger data-testid="note-patient-select">
                    <SelectValue placeholder={`Sélectionner un ${patientLabel}`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {patients.map(patient => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.prenom} {patient.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label htmlFor="note-titre">Titre *</Label>
              <Input
                id="note-titre"
                value={note.titre}
                onChange={(e) => setNote({...note, titre: e.target.value})}
                placeholder="Titre de la note..."
                required
                data-testid="note-title-input"
              />
            </div>

            {statusesEnabled && noteStatuses.length > 0 && (
              <div>
                <Label htmlFor="note-status">Statut</Label>
                <Select 
                  value={note.status_id || ''} 
                  onValueChange={(value) => setNote({...note, status_id: value})}
                >
                  <SelectTrigger data-testid="note-status-select">
                    <SelectValue placeholder="Sélectionner un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Aucun statut</SelectItem>
                    {noteStatuses.map(status => (
                      <SelectItem key={status.id} value={status.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: status.color }}
                          />
                          {status.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Le statut par défaut sera appliqué si aucun n'est sélectionné
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="note-contenu">Contenu *</Label>
              <RichTextEditor
                value={note.contenu}
                onChange={(value) => setNote(prev => ({...prev, contenu: value}))}
                placeholder="Rédigez votre note ici..."
              />
            </div>
            
            <div className="flex-shrink-0 pt-4 sticky bottom-0 bg-white">
              <Button type="submit" className="w-full" data-testid="submit-note-btn">
                {isEditing ? 'Modifier la note' : 'Créer la note'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NoteFormDialog;
